import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { GenericPopupProps } from '@models/ui';
import { NavLink, useNavigate } from '@remix-run/react';
import { mixpanelService } from '@utilities';
import React, { useEffect, Fragment } from 'react';

const SubscribePopup: React.FC<GenericPopupProps> = ({ open, onClose }) => {
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem('position', 'reaction_books');
  }, []);

  const onSubscribe = () => {
    onClose();
    mixpanelService.track('direct_subscribe_cta_click', {
      position: 'video_library',
    });
    setTimeout(() => {
      navigate('/subscribe');
    }, 50);
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog className="relative z-10" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-40 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen ">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                <div className="absolute right-0 top-0 block pt-2 pr-2">
                  <button
                    type="button"
                    // className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-8 w-8" aria-hidden="true" />
                  </button>
                </div>
                <div className="flex flex-col gap-y-5 p-14 items-center justify-center">
                  <DialogTitle as="div" className="flex flex-col gap-y-5 items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="49" height="69" viewBox="0 0 49 69" fill="none">
                      <path
                        d="M43.9599 31.8516H37.3394V20.4643C37.3394 13.3822 31.5777 7.62049 24.4956 7.62049C17.4135 7.62049 11.6517 13.3822 11.6517 20.4643V31.8516H5.03125V20.4643C5.03125 9.73164 13.7629 1 24.4956 1C35.2282 1 43.9599 9.73164 43.9599 20.4643V31.8516Z"
                        fill="#D3D3D3"
                      />
                      <path
                        d="M43.965 20.4645V31.8518H37.3444V26.9881C38.9419 25.1187 42.8359 19.2555 37.5992 11.1088C32.1775 2.67571 22.6794 2.39812 14.8828 3.54704C17.7206 1.92691 21.0052 1 24.5006 1C35.233 1 43.965 9.73194 43.965 20.4645Z"
                        fill="#BCBCBC"
                      />
                      <path
                        d="M24.5005 7.62061C17.4187 7.62061 11.6565 13.3826 11.6565 20.4644V31.8518H6.70312V29.4754C11.8288 29.4756 4.99475 7.62061 24.5005 7.62061Z"
                        fill="#BCBCBC"
                      />
                      <path
                        d="M45.1114 67.9996H3.88586C2.25317 67.9996 0.929688 66.6761 0.929688 65.0434V32.4244C0.929688 30.7917 2.25317 29.4683 3.88586 29.4683H45.1116C46.7443 29.4683 48.0678 30.7917 48.0678 32.4244V65.0434C48.0676 66.6761 46.7441 67.9996 45.1114 67.9996Z"
                        fill="#F3C84B"
                      />
                      <path
                        d="M48.0682 32.3158V65.1524C48.0682 66.7242 46.7925 67.9999 45.2207 67.9999H3.77722C2.20542 67.9999 0.929688 66.7242 0.929688 65.1524V60.6717C1.65011 62.0215 3.26462 64.0731 6.41693 64.0731H30.167C35.4507 64.0731 39.7333 59.7903 39.7333 54.5067V43.4783C39.7333 36.5047 34.3029 30.4394 27.5287 29.4683H45.2205C46.7925 29.4683 48.0682 30.7425 48.0682 32.3158Z"
                        fill="#E59C0C"
                      />
                      <path
                        d="M29.2581 54.806C29.4699 56.1367 28.4425 57.3403 27.0958 57.3403H21.9C20.5533 57.3403 19.5259 56.1367 19.7377 54.806L20.4766 50.1689C19.3644 49.1071 18.6719 47.6108 18.6719 45.953C18.6719 42.7355 21.2804 40.127 24.4979 40.127C27.7154 40.127 30.3239 42.7355 30.3239 45.953C30.3239 47.6108 29.6314 49.1071 28.5192 50.1689L29.2581 54.806Z"
                        fill="#CA8A7C"
                      />
                      <path d="M7.69496 32.8926H3.28125V53.5373H7.69496V32.8926Z" fill="#FFDF78" />
                      <path d="M7.69496 55.6729H3.28125V59.0899H7.69496V55.6729Z" fill="#FFDF78" />
                      <path
                        d="M20.8189 46.9905C20.8189 48.6478 21.5108 50.1442 22.6243 51.2063L21.8854 55.8435C21.7985 56.3888 21.9195 56.9128 22.1857 57.34H21.8996C20.5541 57.34 19.5262 56.1369 19.7383 54.8056L20.4772 50.1684C19.3638 49.1063 18.6719 47.6113 18.6719 45.9526C18.6719 42.7348 21.2803 40.1265 24.498 40.1265C25.8365 40.1265 27.0693 40.5777 28.0533 41.3367C27.602 41.2242 27.1307 41.1644 26.6451 41.1644C23.4273 41.1645 20.8189 43.7729 20.8189 46.9905Z"
                        fill="#845F14"
                      />
                      <path
                        d="M8.68163 18.6549C9.24877 15.2245 10.9802 9.78237 16.2397 6.55276L15.3853 4.84424C15.3853 4.84424 7.9454 8.62326 6.61719 18.6549H8.68163Z"
                        fill="white"
                      />
                      <path d="M6.4765 20.0786C6.43807 20.6308 6.41797 21.2002 6.41797 21.7871H8.41131C8.41131 21.7871 8.39313 21.1346 8.49645 20.0786H6.4765Z" fill="white" />
                      <path
                        d="M11.6562 29.4685V20.4646C11.6562 13.3828 17.4183 7.62061 24.5002 7.62061C31.5822 7.62061 37.3442 13.3826 37.3442 20.4646V29.4685"
                        stroke="#4278AB"
                        strokeWidth="1.03466"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5.03125 29.4684V20.4645C5.03125 9.73209 13.7633 1 24.4957 1C35.2281 1 43.9602 9.73209 43.9602 20.4645V29.4684"
                        stroke="#4278AB"
                        strokeWidth="1.03466"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M45.1114 67.9996H3.88586C2.25317 67.9996 0.929688 66.6761 0.929688 65.0434V32.4244C0.929688 30.7917 2.25317 29.4683 3.88586 29.4683H45.1116C46.7443 29.4683 48.0678 30.7917 48.0678 32.4244V65.0434C48.0676 66.6761 46.7441 67.9996 45.1114 67.9996Z"
                        stroke="#4278AB"
                        strokeWidth="1.03466"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M29.2581 54.806C29.4699 56.1367 28.4425 57.3403 27.0958 57.3403H21.9C20.5533 57.3403 19.5259 56.1367 19.7377 54.806L20.4766 50.1689C19.3644 49.1071 18.6719 47.6108 18.6719 45.953C18.6719 42.7355 21.2804 40.127 24.4979 40.127C27.7154 40.127 30.3239 42.7355 30.3239 45.953C30.3239 47.6108 29.6314 49.1071 28.5192 50.1689L29.2581 54.806Z"
                        stroke="#4278AB"
                        strokeWidth="1.03466"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </DialogTitle>
                  <div className="flex flex-col gap-y-3">
                    <p className="font-bold text-[36px]/[100%] text-center ">Subscribe to view the full video</p>
                    <p className="flex-1 font-normal text-[16px] leading-5 text-center">Finding this interesting? We&apos;ve got a lot more to cover!</p>
                  </div>
                  <NavLink className="header-start-assessment mt-2" to="/subscribe" onClick={onSubscribe}>
                    View Plans
                  </NavLink>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SubscribePopup;
